import { z } from 'zod';

/** Absolute URL, or a path on this origin (local dev serves map files from /map/). */
const urlOrPath = z.union([z.url(), z.string().regex(/^\/[^\s]*$/)]);

const envSchema = z.object({
	VITE_API_URL: z.url(),
	/** PMTiles archive (D32). */
	VITE_MAP_TILES_URL: urlOrPath,
	/** Folder with basemap fonts/ and sprites/ (D32). */
	VITE_MAP_ASSETS_URL: urlOrPath,
	/** Bot and Mini App short name for share links: t.me/<bot>/<app>?startapp=… */
	VITE_BOT_USERNAME: z.string().regex(/^[A-Za-z0-9_]{5,32}$/),
	VITE_MINI_APP_NAME: z.string().regex(/^[A-Za-z0-9_]{3,30}$/),
	/** PostHog project key (public); analytics are off without it. */
	VITE_POSTHOG_KEY: z
		.string()
		.optional()
		.transform((value) => (value === undefined || value.trim() === '' ? undefined : value)),
	VITE_POSTHOG_HOST: z.url().default('https://eu.i.posthog.com'),
	/** Support / appeals contact (T-1005): a Telegram username without @; no link without it. */
	VITE_SUPPORT_TELEGRAM: z
		.string()
		.optional()
		.transform((value) => (value === undefined || value.trim() === '' ? undefined : value))
		.pipe(
			z
				.string()
				.regex(/^[A-Za-z0-9_]{5,32}$/)
				.optional(),
		),
	/** DEV ONLY: signed initData for running outside Telegram (see .env.example). */
	VITE_DEV_INIT_DATA: z
		.string()
		.optional()
		.transform((value) => (value === undefined || value.trim() === '' ? undefined : value)),
});

export type WebEnv = z.infer<typeof envSchema> & { DEV: boolean };

/**
 * Reads each variable by name. Never pass the whole `import.meta.env`: Vite would inline every
 * VITE_ value into the bundle. The dev initData is only read when `DEV` is true, so production
 * builds drop it even if apps/web/.env.local exists at build time.
 */
function viteEnv(): Record<string, unknown> {
	return {
		VITE_API_URL: import.meta.env.VITE_API_URL,
		VITE_MAP_TILES_URL: import.meta.env.VITE_MAP_TILES_URL,
		VITE_MAP_ASSETS_URL: import.meta.env.VITE_MAP_ASSETS_URL,
		VITE_BOT_USERNAME: import.meta.env.VITE_BOT_USERNAME,
		VITE_MINI_APP_NAME: import.meta.env.VITE_MINI_APP_NAME,
		VITE_POSTHOG_KEY: import.meta.env.VITE_POSTHOG_KEY,
		VITE_POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST,
		VITE_SUPPORT_TELEGRAM: import.meta.env.VITE_SUPPORT_TELEGRAM,
		VITE_DEV_INIT_DATA: import.meta.env.DEV ? import.meta.env.VITE_DEV_INIT_DATA : undefined,
		DEV: import.meta.env.DEV,
	};
}

/** Validates the public build-time env once at startup (fail fast with a readable message). */
export function readEnv(source: Record<string, unknown> = viteEnv()): WebEnv {
	const result = envSchema.safeParse(source);
	if (!result.success) {
		const keys = result.error.issues.map((issue) => issue.path.join('.')).join(', ');
		throw new Error(`Invalid web env: ${keys}`);
	}
	return { ...result.data, DEV: source.DEV === true };
}
